/**
 * Keyboard access for scrolling boxes (WCAG 2.1.1; axe scrollable-region-focusable).
 *
 * A box that scrolls has to be reachable by keyboard. Links and buttons inside it
 * get there on their own: Tab lands on them and the browser scrolls them into view.
 * A box of text, images or a table has nothing that takes focus, so a keyboard user
 * can't scroll it at all. The box itself has to take focus, so the arrow keys can.
 *
 * Opt in with data-scroll-region="<name>" on the scrolling element; the value is its
 * accessible name. While the box scrolls this adds tabindex="0", aria-label="<name>"
 * and, on a <div> or <span>, role="region". "Scrolls" means what axe means: overflow
 * auto or scroll on an axis, with content more than 13px past that edge, on a box
 * that is rendered. When the box stops scrolling (a wider screen, a closed accordion,
 * a shorter list) the attributes come off again, so no box ever stays a tab stop that
 * does nothing. IBM's checker fails a tab stop with a non-widget role unless its box
 * really scrolls. Attributes the markup already sets are left alone: a list keeps
 * role="list", and an aria-labelledby wins over the name (the value may then be empty).
 * data-scroll-region-owns records which ones the script manages, so a box that leaves
 * the page and comes back (the family switcher's cache) still knows.
 *
 * By default the box takes focus whenever it scrolls, even with links inside: one
 * link at the bottom of a wide table doesn't let anyone scroll to its right-hand
 * columns. Add data-scroll-region-unless-focusable when the box's own buttons or
 * links cover all of it (a row of play buttons); then it takes focus only while it
 * holds no tab stop at all.
 *
 * Re-checked when a box or its children change size (a resize, an accordion or popup
 * opening), when a <details> toggles, after the page and its fonts load, and when a
 * box is added to the page (a product-family swap, the homepage switcher, a section
 * reloaded in the theme editor).
 */
(function () {
  'use strict';
  if (window.JiYuScrollRegions) return;

  var SELECTOR = '[data-scroll-region]';
  var ACTIVE = 'data-scroll-region-active';
  var OWNS = 'data-scroll-region-owns';
  // axe's getScroll() buffer: a box counts as scrolling past 13px of overflow.
  var THRESHOLD = 13;
  var TAB_STOPS =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, iframe, audio[controls], video[controls], ' +
    '[contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])';

  var regions = new Set();
  var waitingForBlur = new WeakSet();
  var resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(onResize) : null;

  // Rendered and visible. visibility: hidden counts as hidden: a tabbed section's
  // inactive panels are collapsed to width 0 that way, which reads as a box that
  // scrolls (seen in section-ingredient). The size floor is IBM's own for "scrollable",
  // so the checker never meets a tab stop it doesn't consider a scroll box.
  function isRendered(el) {
    var visible = typeof el.checkVisibility === 'function'
      ? el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true })
      : el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    if (!visible) return false;
    var w = el.offsetWidth, h = el.offsetHeight;
    return Math.max(w, h) >= 30 && Math.min(w, h) >= 15;
  }

  function scrolls(el) {
    if (!isRendered(el)) return false;
    var style = getComputedStyle(el);
    var x = style.overflowX === 'auto' || style.overflowX === 'scroll';
    var y = style.overflowY === 'auto' || style.overflowY === 'scroll';
    return (
      (x && el.scrollWidth > el.clientWidth + THRESHOLD) ||
      (y && el.scrollHeight > el.clientHeight + THRESHOLD)
    );
  }

  function holdsTabStop(el) {
    var candidates = el.querySelectorAll(TAB_STOPS);
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c.tabIndex >= 0 && c.getClientRects().length && getComputedStyle(c).visibility !== 'hidden') return true;
    }
    return false;
  }

  // Which of tabindex, role and aria-label this script manages on a box: the ones its
  // markup doesn't set. Worked out once, before the script has touched the box.
  function owns(el) {
    var list = el.getAttribute(OWNS);
    if (list === null) {
      var own = [];
      if (!el.hasAttribute('tabindex')) own.push('tabindex');
      if (!el.hasAttribute('role') && (el.tagName === 'DIV' || el.tagName === 'SPAN')) own.push('role');
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) own.push('aria-label');
      list = own.join(' ');
      el.setAttribute(OWNS, list);
    }
    return list.split(' ');
  }

  function update(el) {
    if (!el.isConnected) {
      forget(el);
      return;
    }
    var own = owns(el);
    var needsFocus = scrolls(el) && !(el.hasAttribute('data-scroll-region-unless-focusable') && holdsTabStop(el));
    if (needsFocus) {
      if (!el.hasAttribute(ACTIVE)) el.setAttribute(ACTIVE, '');
      if (own.indexOf('tabindex') !== -1 && el.getAttribute('tabindex') !== '0') el.setAttribute('tabindex', '0');
      if (own.indexOf('role') !== -1 && el.getAttribute('role') !== 'region') el.setAttribute('role', 'region');
      var name = el.getAttribute('data-scroll-region');
      if (own.indexOf('aria-label') !== -1 && name && el.getAttribute('aria-label') !== name) el.setAttribute('aria-label', name);
    } else if (el.hasAttribute(ACTIVE)) {
      // Never pull the tab stop out from under someone who is on it; try again when
      // focus leaves.
      if (el.contains(document.activeElement)) {
        if (!waitingForBlur.has(el)) {
          waitingForBlur.add(el);
          el.addEventListener('focusout', function retry() {
            el.removeEventListener('focusout', retry);
            waitingForBlur.delete(el);
            setTimeout(function () { update(el); }, 0);
          });
        }
        return;
      }
      el.removeAttribute(ACTIVE);
      own.forEach(function (attr) {
        if (attr) el.removeAttribute(attr);
      });
    }
  }

  function onResize(entries) {
    var seen = new Set();
    entries.forEach(function (entry) {
      var region = entry.target.closest(SELECTOR);
      if (region && !seen.has(region)) {
        seen.add(region);
        update(region);
      }
    });
  }

  function register(el) {
    if (regions.has(el)) return;
    regions.add(el);
    owns(el);
    if (resizeObserver) {
      // The first observation fires once the box is laid out, which runs the first
      // update(). Children too: a row whose cards grow (a font swap) changes how far
      // it scrolls without the box itself changing size.
      resizeObserver.observe(el);
      for (var i = 0; i < el.children.length; i++) resizeObserver.observe(el.children[i]);
    } else {
      update(el);
    }
  }

  function forget(el) {
    regions.delete(el);
    if (!resizeObserver) return;
    resizeObserver.unobserve(el);
    for (var i = 0; i < el.children.length; i++) resizeObserver.unobserve(el.children[i]);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches(SELECTOR)) register(root);
    root.querySelectorAll(SELECTOR).forEach(register);
  }

  function refresh() {
    regions.forEach(update);
  }

  scan(document.documentElement);

  // Regions added later: the PDP family swap and the homepage switcher insert whole
  // product sections, and the theme editor reloads sections. childList only, so the
  // attributes update() sets can't feed back into this observer. A swapped-out region
  // is dropped; the family switcher's cache can put it back, and that insertion
  // registers it again.
  new MutationObserver(function (records) {
    var removed = false;
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) scan(added[j]);
      if (records[i].removedNodes.length) removed = true;
    }
    if (removed) {
      regions.forEach(function (el) {
        if (!el.isConnected) forget(el);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // A closed <details> skips rendering its content, so an accordion panel's size can
  // read the same closed and open. `toggle` doesn't bubble; listen in the capture phase.
  document.addEventListener(
    'toggle',
    function (event) {
      var target = event.target;
      if (!target || target.nodeType !== 1) return;
      regions.forEach(function (el) {
        if (target.contains(el)) update(el);
      });
    },
    true
  );

  window.addEventListener('load', refresh);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  window.JiYuScrollRegions = { scan: scan, refresh: refresh };
})();
